import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { connect } from 'react-redux';
import { Card, CardContent } from '@ferris/react/components/cards';
import {
    isEnUsPermanentFeatureVrEnabled,
    isValuePropBannerActive
} from '../../../utils/ExperimentsUtil';
import ExperimentUtil from '../../../utils/ExperimentUtil';
import TranslationUtil from '../../../utils/TranslationUtil';
import HeroV2 from './HeroV2';
import { HERO_IMAGE_V2 } from '../../../../server/utils/Cms/constants';
import ValuePropBanner from './ValuePropBanner';
import HeroV4 from './HeroV4';
import { europeanUnion } from '../../../utils/LocaleUtil/countryList';

const HERO_IMAGES = {
    cl: {
        desktop: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/ulx-hero-desktop.webp',
        mobile: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/ulx-hero-mobile.webp'
    },
    vr: {
        desktop: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/Desktop-header-no-scrim_new_branding_V2.webp',
        mobile: 'https://headless.expediagroup.com/content/dam/marketing/headless/images/home-page/Mobile-header-no-scrim-new_branding_V2.webp'
    }
};

const REBRANDED_ALT = 'View of guest walking past the check-in desk at Maritime Hotel';

const Picture = React.memo(({
    isOkcTemplate,
    heroImgToUse,
    url,
    mobileUrl,
    alt,
    isFrboFlow
}) => (
    <picture>
        <source
            media="(min-width: 480px)"
            srcSet={(!isFrboFlow && !isOkcTemplate) ? heroImgToUse.desktop : url}
        />
        <img
            src={(!isFrboFlow && !isOkcTemplate) ? heroImgToUse.mobile : mobileUrl}
            alt={(!isFrboFlow && !isOkcTemplate) ? REBRANDED_ALT : alt}
        />
    </picture>
));

Picture.propTypes = {
    isOkcTemplate: PropTypes.bool.isRequired,
    heroImgToUse: PropTypes.shape({
        desktop: PropTypes.string.isRequired,
        mobile: PropTypes.string.isRequired
    }).isRequired,
    url: PropTypes.string.isRequired,
    mobileUrl: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
    isFrboFlow: PropTypes.bool.isRequired
};

export const Hero = React.memo((props) => {
    const {
        image,
        translations,
        isFrboFlow,
        templateId,
        children,
        heroV2Props,
        valuePropBannerProps
    } = props;
    // Prefer AEM-provided hero image for OKC template (do not override with static VR images)
    const isOkcTemplate = templateId === 'aem-okc';
    const useStaticVrboHero = isFrboFlow && !isOkcTemplate;
    const { url, alt, mobileUrl: providedMobileUrl } = useStaticVrboHero ? HERO_IMAGE_V2 : image;
    // Prefer explicit mobileUrl from AEM for OKC; fallback to desktop url
    const mobileUrl = useStaticVrboHero
        ? HERO_IMAGE_V2.mobileSrc
        : (providedMobileUrl || image.url);

    const clBrandingTitle = translations.getTranslation('cl.lyp.branding.hero.title1');
    const clBrandingSubtitle = translations.getTranslation('cl.lyp.branding.hero.subtitle');

    const heroV4Props = useMemo(() => (
        isFrboFlow ? {
            copy: heroV2Props.copy,
            titleList: heroV2Props.titleList,
            titleSuffix: heroV2Props.titleSuffix,
            accessibilityButton: heroV2Props.accessibilityButton
        } : {
            copy: clBrandingSubtitle,
            titleList: [clBrandingTitle]
        }
    ), [
        isFrboFlow,
        heroV2Props.copy,
        heroV2Props.titleList,
        heroV2Props.titleSuffix,
        heroV2Props.accessibilityButton,
        clBrandingSubtitle,
        clBrandingTitle
    ]);

    const heroImgToUse = isFrboFlow ? HERO_IMAGES.vr : HERO_IMAGES.cl;
    return (
        <>
            <ValuePropBanner
                {...valuePropBannerProps}
                okc={isOkcTemplate}
            />
            <section id="ulx-hero">
                <div className={cn('ulx__aem-hero__background', 'redesigned')}>
                    <Picture
                        isOkcTemplate={isOkcTemplate}
                        heroImgToUse={heroImgToUse}
                        url={url}
                        mobileUrl={mobileUrl}
                        alt={alt}
                        isFrboFlow={isFrboFlow}
                    />
                    <div className={cn('mask')} />
                </div>
                <div className={cn('ulx__aem-hero', 'ulx__aem-hero-redesigned')}>
                    <HeroV4
                        display={!isFrboFlow}
                        {...heroV4Props}
                    />
                    <HeroV2
                        display={isFrboFlow}
                        copy={heroV2Props.copy}
                        titleList={heroV2Props.titleList}
                        titleSuffix={heroV2Props.titleSuffix}
                        accessibilityButton={heroV2Props.accessibilityButton}
                        okcOfferLink={isOkcTemplate ? heroV2Props.okcOfferLink : null}
                    />
                    <div className={cn('ulx__aem-wizard')} id="list-your-property-form">
                        <section>
                            <Card elevation="0" theme="default">
                                <CardContent
                                    collapsePadding={false}
                                    separatorBottom={false}
                                    separatorTop={false}
                                >
                                    {children}
                                </CardContent>
                            </Card>
                        </section>
                    </div>
                </div>
            </section>
        </>
    );
}, (prevProps, nextProps) => (
    // Only rerender if key props that affect hero image or content change
    prevProps.image === nextProps.image
        && prevProps.isFrboFlow === nextProps.isFrboFlow
        && prevProps.templateId === nextProps.templateId
        && prevProps.heroV2Props === nextProps.heroV2Props
        && prevProps.valuePropBannerProps === nextProps.valuePropBannerProps
        && prevProps.translations === nextProps.translations
    // Note: children (LeadForm) changes are allowed to rerender
));

function mapStateToProps(state, ownProps) {
    const { app } = state;
    const {
        features,
        displayBrand,
        locale,
        templateId
    } = app;
    const translations = new TranslationUtil(
        app.translations,
        app.fallbackTranslations
    );
    const experimentsUtil = new ExperimentUtil(app.experiments);
    const contentMessagingUpdatesEnabled = isEnUsPermanentFeatureVrEnabled(experimentsUtil);
    const { isFrboFlow } = features;
    const country = locale.display.slice(-2).toUpperCase();
    const isEu = europeanUnion.includes(country);
    // Default heroV2 props from translations
    const defaultHeroV2Props = {
        copy: translations.getTranslation('Vrbo.lyp.hero.copy.V2', { brand: displayBrand }),
        titleSuffix: translations.getTranslation('Vrbo.lyp.hero.title.suffix.V2', { brand: displayBrand }),
        titleList: [
            translations.getTranslation('Vrbo.lyp.hero.title1.V2'),
            translations.getTranslation('Vrbo.lyp.hero.title2.V2'),
            translations.getTranslation('Vrbo.lyp.hero.title3.V2')
        ],
        accessibilityButton: isEu ? {
            play: translations.getTranslation('accessibility.button.play.animation'),
            pause: translations.getTranslation('accessibility.button.pause.animation')
        } : null,
        okcOfferLink: translations.getTranslation('Vrbo.lyp.hero.okcOffer.link')
    };

    // If a parent component provides heroV2Props (e.g., OKC AEM template), prefer those
    // and fall back to defaults for any missing keys.
    const heroV2Props = ownProps && ownProps.heroV2Props
        ? { ...defaultHeroV2Props, ...ownProps.heroV2Props }
        : defaultHeroV2Props;

    // Default banner from experiments/translations
    const defaultValuePropBannerProps = {
        show: isValuePropBannerActive(experimentsUtil),
        copy: isFrboFlow
            ? translations.getTranslation('Vrbo.valuePropBanner.text')
            : translations.getTranslation('CL.valuePropBanner.text')
    };

    // If parent provides valuePropBannerProps (e.g., via AEM OKC hero), prefer those values
    const valuePropBannerProps = ownProps && ownProps.valuePropBannerProps
        ? { ...defaultValuePropBannerProps, ...ownProps.valuePropBannerProps }
        : defaultValuePropBannerProps;

    return {
        valuePropBannerProps,
        contentMessagingUpdatesEnabled,
        translations,
        isFrboFlow,
        heroV2Props,
        displayBrand,
        templateId
    };
}
Hero.propTypes = {
    valuePropBannerProps: PropTypes.shape({
        show: PropTypes.bool.isRequired,
        copy: PropTypes.string.isRequired
    }).isRequired,
    image: PropTypes.shape({
        url: PropTypes.string.isRequired,
        mobileUrl: PropTypes.string,
        alt: PropTypes.string
    }).isRequired,
    contentMessagingUpdatesEnabled: PropTypes.bool.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    translations: PropTypes.object.isRequired,
    isFrboFlow: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired,
    heroV2Props: PropTypes.shape({
        copy: PropTypes.string.isRequired,
        titleSuffix: PropTypes.string.isRequired,
        titleList: PropTypes.arrayOf(PropTypes.string).isRequired,
        accessibilityButton: PropTypes.shape({
            play: PropTypes.string.isRequired,
            pause: PropTypes.string.isRequired
        }),
        okcOfferLink: PropTypes.string
    }).isRequired,
    displayBrand: PropTypes.string.isRequired,
    templateId: PropTypes.string.isRequired
};

Hero.defaultProps = {};
export default connect(mapStateToProps)(Hero);
